import React, { useState } from 'react';
import {
  FileText,
  Plus,
  MoreVertical,
  Check,
  X,
  Edit,
  Copy,
  RefreshCw,
  Users,
} from 'lucide-react';
import { CreateEventModal } from './CreateEventModal';

interface SchoolEvent {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  type: 'meeting' | 'event' | 'class';
  status: 'approved' | 'pending' | 'declined';
  participants: number;
  createdBy: string;
}

const mockEvents: SchoolEvent[] = [
  {
    id: '1',
    title: 'Strategic Vision Board Meeting',
    description: '9th Grade strategic planning session',
    date: '27 Dec',
    time: '09:00 - 11:00',
    type: 'meeting',
    status: 'approved',
    participants: 12,
    createdBy: 'Admin',
  },
  {
    id: '2',
    title: 'Winter Training',
    description: 'Annual winter sports training',
    date: '27 Dec',
    time: '14:00 - 16:00',
    type: 'event',
    status: 'approved',
    participants: 45,
    createdBy: 'Admin',
  },
  {
    id: '3',
    title: 'Parent-Teacher Conference',
    description: 'Q4 academic review meeting',
    date: '29 Dec',
    time: '10:00 - 12:00',
    type: 'meeting',
    status: 'pending',
    participants: 30,
    createdBy: 'Teacher',
  },
  {
    id: '4',
    title: 'Science Fair Exhibition',
    description: 'Annual science project showcase',
    date: '29 Dec',
    time: '09:00 - 17:00',
    type: 'event',
    status: 'approved',
    participants: 150,
    createdBy: 'Admin',
  },
  {
    id: '5',
    title: 'Staff Meeting',
    description: 'Monthly staff sync',
    date: '30 Dec',
    time: '08:00 - 09:00',
    type: 'meeting',
    status: 'declined',
    participants: 25,
    createdBy: 'Teacher',
  },
];

export function SchoolEventsTab() {
  const [events, setEvents] = useState(mockEvents);
  const [contextMenuId, setContextMenuId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<SchoolEvent | null>(null);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'declined':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'meeting':
        return 'bg-blue-100 text-blue-700';
      case 'event':
        return 'bg-purple-100 text-purple-700';
      case 'class':
        return 'bg-teal-100 text-teal-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const handleApprove = (id: string) => {
    setEvents((prev) =>
      prev.map((e) => (e.id === id ? { ...e, status: 'approved' as const } : e))
    );
  };

  const handleDecline = (id: string) => {
    setEvents((prev) =>
      prev.map((e) => (e.id === id ? { ...e, status: 'declined' as const } : e))
    );
  };

  const handleResetStatus = (id: string) => {
    setEvents((prev) =>
      prev.map((e) => (e.id === id ? { ...e, status: 'pending' as const } : e))
    );
  };

  const copyEventLink = (id: string) => {
    navigator.clipboard.writeText(`${window.location.origin}/event/${id}`);
    // You could show a toast notification here
  };

  return (
    <div className="space-y-4">
      {/* Header Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="text-sm text-gray-500">
          {events.length} Events
        </div>

        <div className="flex items-center gap-3">
          {/* Reports */}
          <button
            onClick={() => alert('Reports feature coming soon!')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <FileText className="w-4 h-4" />
            Reports
          </button>

          {/* New Event */}
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700"
          >
            <Plus className="w-4 h-4" />
            New Event
          </button>
        </div>
      </div>

      {/* Events List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="divide-y divide-gray-200">
          {events.map((event) => (
            <div key={event.id} className="p-4 hover:bg-gray-50">
              <div className="flex items-start gap-4">
                {/* Date Box */}
                <div className="flex-shrink-0 w-16 h-16 rounded-lg bg-teal-50 flex flex-col items-center justify-center">
                  <span className="text-lg font-bold text-teal-600">
                    {event.date.split(' ')[0]}
                  </span>
                  <span className="text-xs text-teal-600">
                    {event.date.split(' ')[1]}
                  </span>
                </div>

                {/* Event Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-gray-900">{event.title}</h4>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getTypeBadge(event.type)}`}>
                      {event.type.charAt(0).toUpperCase() + event.type.slice(1)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mb-2">{event.description}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>{event.time}</span>
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {event.participants} participants
                    </span>
                  </div>
                </div>

                {/* Status & Actions */}
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusBadge(event.status)}`}>
                    {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                  </span>

                  {/* Quick Actions for Pending Events */}
                  {event.status === 'pending' && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleApprove(event.id)}
                        className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                        title="Approve"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDecline(event.id)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        title="Decline"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {/* Context Menu */}
                  <div className="relative">
                    <button
                      onClick={() => setContextMenuId(contextMenuId === event.id ? null : event.id)}
                      className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    {contextMenuId === event.id && (
                      <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                        <button
                          onClick={() => {
                            setEditingEvent(event);
                            setShowCreateModal(true);
                            setContextMenuId(null);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <Edit className="w-4 h-4" />
                          Edit Event
                        </button>
                        <button
                          onClick={() => copyEventLink(event.id)}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <Copy className="w-4 h-4" />
                          Copy Event Link
                        </button>
                        {event.status !== 'pending' && (
                          <button
                            onClick={() => handleResetStatus(event.id)}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <RefreshCw className="w-4 h-4" />
                            Reset Status
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create/Edit Event Modal */}
      {showCreateModal && (
        <CreateEventModal
          onClose={() => {
            setShowCreateModal(false);
            setEditingEvent(null);
          }}
          onCreated={() => {
            // Event was created/updated via API
            // For now, just close the modal - in production, you'd refresh the events list
            setShowCreateModal(false);
            setEditingEvent(null);
          }}
        />
      )}
    </div>
  );
}
